import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { T } from './theme';

const API = 'https://tcio-api.onrender.com';
const card = { background:T.surface, border:`1px solid ${T.b200}`, borderRadius:10,
  boxShadow:'0 1px 2px rgba(110,59,71,.06), 0 4px 14px rgba(110,59,71,.07)' };
const label = { fontSize:10, letterSpacing:'1.1px', textTransform:'uppercase',
  color:T.inkSoft, fontWeight:600 };
const btn = (primary) => ({ borderRadius:8, padding:'9px 16px', font:'inherit',
  fontWeight:600, fontSize:13, cursor:'pointer',
  background: primary ? T.rose : '#fff', color: primary ? '#fff' : T.rose,
  border: primary ? 'none' : `1px solid ${T.b300}` });
const input = { border:`1px solid ${T.b200}`, borderRadius:8, padding:'8px 11px',
  font:'inherit', color:T.ink, background:'#fff', boxSizing:'border-box' };

const TYPE_LABEL = { progress_note:'Progress Note', group_note:'Group Note', assessment:'Assessment',
  treatment_plan:'Treatment Plan', treatment_plan_review:'TP Review', discharge_summary:'Discharge Summary',
  case_mgmt_note:'Case Mgmt Note', family_note:'Family Note', ur_review:'UR Review' };

// Renders a note body: narrative_text if present, else the content laid out by the FORM DEFINITION —
// sections and fields in the form's own order, with the form's own labels (Erin 71a9a22b, 10/24).
// Multi-choice answers (arrays) are shown joined; anything in the content the form does not know goes at the end.
const FORM_CACHE = {};
function useFormDef(formKey) {
  const [def, setDef] = useState(formKey ? FORM_CACHE[formKey] || null : null);
  useEffect(() => {
    if (!formKey) return;
    if (FORM_CACHE[formKey]) { setDef(FORM_CACHE[formKey]); return; }
    let live = true;
    axios.get(`${API}/api/emr/forms/${encodeURIComponent(formKey)}`).then(r => {
      const d = r.data || {}; const row = d.form || d.data || d;
      const fields = Array.isArray(row.fields) ? row.fields : (row.definition && Array.isArray(row.definition.fields) ? row.definition.fields : []);
      const out = { title: row.title || row.name || row.display_name || null, fields };
      FORM_CACHE[formKey] = out; if (live) setDef(out);
    }).catch(() => { if (live) setDef({ title: null, fields: [] }); });
    return () => { live = false; };
  }, [formKey]);
  return def;
}
const showVal = (v) => Array.isArray(v) ? v.join(', ') : (v && typeof v === 'object') ? Object.entries(v).filter(([, x]) => x != null && x !== '').map(([k, x]) => k.replace(/_/g, ' ') + ': ' + (Array.isArray(x) ? x.join(', ') : String(x))).join(' · ') : String(v);
const hasVal = (v) => v != null && v !== '' && !(Array.isArray(v) && !v.length);
function NoteBody({ note }) {
  const def = useFormDef(note.form_key);
  if (note.narrative_text) {
    return <div style={{ fontSize:13.5, lineHeight:1.65, color:T.ink, whiteSpace:'pre-wrap' }}>{note.narrative_text}</div>;
  }
  const c = note.content || {};
  const fields = (def && def.fields) || [];
  const known = new Set(fields.map(x => x.key));
  const rows = []; let lastGroup = null;
  for (const fd of fields) {
    if (!hasVal(c[fd.key])) continue;
    if (fd.group && fd.group !== lastGroup) { rows.push({ section: fd.group }); lastGroup = fd.group; }
    rows.push({ k: fd.key, label: fd.label || fd.key.replace(/_/g, ' '), v: c[fd.key] });
  }
  const extra = Object.entries(c).filter(([k, v]) => !known.has(k) && hasVal(v));
  if (extra.length) { if (fields.length) rows.push({ section: 'Other entries' }); for (const [k, v] of extra) rows.push({ k, label: k.replace(/_/g, ' '), v }); }
  if (!rows.length) return <div style={{ color:T.inkSoft, fontSize:13 }}>{def ? 'No narrative content on this version.' : 'Loading…'}</div>;
  return (
    <div>
      {def && def.title && <div style={{ fontSize:14, fontWeight:700, color:T.ink, marginBottom:10 }}>{def.title}</div>}
      <div style={{ display:'grid', gridTemplateColumns:'240px 1fr', gap:'6px 14px', fontSize:13 }}>
        {rows.map((r, i) => r.section
          ? <div key={'s' + i} style={{ gridColumn:'1 / -1', marginTop: i ? 12 : 0, paddingBottom:4, borderBottom:`1px solid ${T.b200}`, fontSize:12.5, fontWeight:700, color:T.ink }}>{r.section}</div>
          : <React.Fragment key={r.k}>
              <div style={{ fontSize:12, color:T.inkSoft, alignSelf:'start', paddingTop:2 }}>{r.label}</div>
              <div style={{ color:T.ink, whiteSpace:'pre-wrap' }}>{showVal(r.v)}</div>
            </React.Fragment>)}
      </div>
    </div>
  );
}

export default function CosignQueue({ onOpenChart, user }) {
  const isOversight = ['ceo','admin'].includes((user && user.role) || '');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [passing, setPassing] = useState(false);   // river mode on/off
  const [idx, setIdx] = useState(0);               // current item index in the river
  const [pin, setPin] = useState('');
  const [pinHeld, setPinHeld] = useState('');      // verified once, reused per cosign (server re-verifies every call)
  const [sigEnrolled, setSigEnrolled] = useState(false);
  const [busy, setBusy] = useState(null);          // note id in flight
  const [err, setErr] = useState('');
  const [done, setDone] = useState(0);
  const [kickFor, setKickFor] = useState(null);    // note id showing kick-back box
  const [kickReason, setKickReason] = useState('');
  const [declineFor, setDeclineFor] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [facilities, setFacilities] = useState([]);
  const [facFilter, setFacFilter] = useState('');
  const [cosigners, setCosigners] = useState([]);
  const [viewAs, setViewAs] = useState('');        // oversight: cosigner id being viewed
  const [flash, setFlash] = useState(null);        // 'advance' | 'complete'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (facFilter) params.facility_id = facFilter;
      if (isOversight && viewAs) params.cosigner_id = viewAs;
      const r = await axios.get(`${API}/api/emr/cosign-queue`, { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setFacilities(r.data.facilities || []);
    } catch (e) { setErr('Could not load queue'); }
    setLoading(false);
  }, [facFilter, viewAs, isOversight]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isOversight) axios.get(`${API}/api/emr/cosigners`)
      .then(r => setCosigners(r.data.cosigners || [])).catch(() => {});
  }, [isOversight]);
  const readOnly = isOversight && !!viewAs;
  useEffect(() => {
    axios.get(`${API}/api/emr/signature/status`)
      .then(r => setSigEnrolled(!!(r.data && r.data.enrolled)))
      .catch(() => {});
  }, []);

  const advance = () => {
    setKickFor(null); setKickReason('');
    setFlash('advance');
    setTimeout(() => {
      setFlash(null);
      setItems(prev => {
        const next = prev.map(it => ({ ...it, notes: it.notes.filter(n => !n._done) }))
          .filter(it => it.notes.length > 0);
        if (!next.length) setFlash('complete');
        return next;
      });
      setIdx(0);
    }, 900);
  };

  const [needEnroll, setNeedEnroll] = useState(false);
  const cosign = async (note) => {
    setBusy(note.id); setErr('');
    const usePin = pinHeld || pin;
    try {
      await axios.post(`${API}/api/emr/notes/${note.id}/sign`,
        sigEnrolled ? { method:'pin', pin: usePin, attestation_text: 'Cosigned via review pass' }
                    : { method:'attestation', attestation_text: 'Cosigned via review pass' });
      if (sigEnrolled && !pinHeld) setPinHeld(usePin);
      setPin('');
      setDone(d => d + 1);
      note._done = true;
      // if every note in the current bundle is done, advance the river
      setItems(prev => [...prev]);
      const cur = items[idx];
      if (cur && cur.notes.every(n => n._done)) advance();
    } catch (e2) {
      const msg = (e2.response && e2.response.data && e2.response.data.error) || 'Cosign failed';
      if (e2.response && e2.response.data && e2.response.data.enroll_required) { setNeedEnroll(true); }
      else if (e2.response && e2.response.status === 401) { setPinHeld(''); setErr('PIN required — enter your 4-digit signature PIN'); }
      else setErr(msg);
    }
    setBusy(null);
  };

  const kickBack = async (note) => {
    if (!kickReason.trim()) { setErr('Kick-back reason required'); return; }
    setBusy(note.id); setErr('');
    try {
      await axios.post(`${API}/api/emr/notes/${note.id}/kickback`, { reason: kickReason.trim() });
      note._done = true;
      setItems(prev => [...prev]);
      const cur = items[idx];
      if (cur && cur.notes.every(n => n._done)) advance();
      else { setKickFor(null); setKickReason(''); }
    } catch (e2) {
      setErr((e2.response && e2.response.data && e2.response.data.error) || 'Kick-back failed');
    }
    setBusy(null);
  };

  const decline = async (note) => {
    if (!declineReason.trim()) { setErr('Reason required'); return; }
    setBusy(note.id); setErr('');
    try {
      await axios.post(`${API}/api/emr/notes/${note.id}/cosign-decline`, { reason: declineReason.trim() });
      note._done = true;
      setItems(prev => [...prev]);
      const cur = items[idx];
      if (cur && cur.notes.every(n => n._done)) advance();
      else { setDeclineFor(null); setDeclineReason(''); }
    } catch (e2) {
      setErr((e2.response && e2.response.data && e2.response.data.error) || 'Decline failed');
    }
    setBusy(null);
  };

  const ago = (h) => h < 24 ? `${h}h` : `${Math.round(h/24)}d`;

  const filterBar = (
    <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
      {isOversight && (
        <select value={viewAs} onChange={e => setViewAs(e.target.value)} style={{ ...input, minWidth:260 }}>
          <option value="">My view</option>
          {cosigners.map(c => (
            <option key={c.id} value={c.id}>
              {c.name} — {c.waiting} waiting{c.oldest_hours >= 24 ? ` · oldest ${Math.round(c.oldest_hours/24)}d` : c.oldest_hours > 0 ? ` · oldest ${c.oldest_hours}h` : ''}
            </option>
          ))}
        </select>
      )}
      {facilities.length > 1 && (
        <select value={facFilter} onChange={e => setFacFilter(e.target.value)} style={{ ...input, minWidth:200 }}>
          <option value="">All facilities</option>
          {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      )}
      {readOnly && <span style={{ ...label, color:T.rose }}>Oversight view — read only</span>}
    </div>
  );

  if (loading) return <div style={{ padding:30, color:T.inkSoft }}>Loading cosign queue…</div>;

  if (needEnroll) return (
    <div style={{ ...card, padding:36, textAlign:'center', maxWidth:560, margin:'40px auto' }}>
      <div style={{ fontSize:18, fontWeight:800, color:T.ink, marginBottom:6 }}>Signature enrollment required</div>
      <div style={{ fontSize:13.5, color:T.inkSoft, lineHeight:1.6 }}>
        Every clinical signature in TCIO carries your drawn signature and PIN — no exceptions.
        Open any note in a patient chart and click Sign to enroll (draw once, set a 4-digit PIN, ~60 seconds),
        then return here to run your cosign pass.
      </div>
      <button style={{ ...btn(false), marginTop:16 }} onClick={() => setNeedEnroll(false)}>Back to queue</button>
    </div>
  );

  // ── Completion state ──
  if (flash === 'complete' || (!items.length && done > 0)) return (
    <div style={{ ...card, padding:40, textAlign:'center', maxWidth:520, margin:'40px auto' }}>
      <div style={{ fontSize:22, fontWeight:700, color:T.rose, marginBottom:8 }}>✓ Queue clear</div>
      <div style={{ color:T.inkSoft, fontSize:14 }}>{done} note{done===1?'':'s'} cosigned this pass. Every signature carries its own timestamp.</div>
      <button style={{ ...btn(false), marginTop:18 }} onClick={() => { setDone(0); setPassing(false); load(); }}>Back to queue</button>
    </div>
  );

  if (!items.length) return (
    <div style={{ maxWidth:860, margin:'0 auto' }}>
    {filterBar}
    <div style={{ ...card, padding:40, textAlign:'center', maxWidth:520, margin:'40px auto' }}>
      <div style={{ fontSize:18, fontWeight:700, color:T.ink }}>{readOnly ? 'Nothing waiting in this queue' : 'Nothing waiting for your cosignature'}</div>
      <div style={{ color:T.inkSoft, fontSize:13.5, marginTop:6 }}>Notes appear here after their author signs. Check back after your team's documentation day.</div>
    </div>
    </div>
  );

  // ── Queue list (pre-pass) ──
  if (!passing) return (
    <div style={{ maxWidth:860, margin:'0 auto' }}>
      {filterBar}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <div>
          <div style={{ fontSize:19, fontWeight:800, color:T.ink }}>Cosign Queue</div>
          <div style={{ fontSize:13, color:T.inkSoft }}>{total} note{total===1?'':'s'} awaiting {readOnly ? 'this cosigner' : 'your cosignature'}</div>
        </div>
        {!readOnly && <button style={btn(true)} onClick={() => { setPassing(true); setIdx(0); setDone(0); }}>Start Review Pass →</button>}
      </div>
      {items.map((it, i) => (
        <div key={i} style={{ ...card, padding:'12px 16px', marginBottom:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:14, color:T.ink }}>
              {it.kind === 'group'
                ? `Group Session — ${it.topic || 'no topic'} · ${it.notes.length} client notes`
                : `${TYPE_LABEL[it.notes[0].note_type] || it.notes[0].note_type} — ${it.notes[0].patient_name || '—'}`}
            </div>
            <div style={{ fontSize:12, color:T.inkSoft, marginTop:2 }}>
              by {it.notes[0].author_name} · waiting {ago(it.notes[0].hours_waiting)}
            </div>
          </div>
          <div style={{ ...label }}>{it.kind === 'group' ? `${it.notes.length}×` : ''}</div>
        </div>
      ))}
    </div>
  );

  // ── The river ──
  const cur = items[idx] || items[0];
  if (!cur) return null;
  const remaining = cur.notes.filter(n => !n._done);
  const position = items.slice(0, idx).reduce((t, it) => t + it.notes.length, 0) + (cur.notes.length - remaining.length) + 1;

  return (
    <div style={{ maxWidth:860, margin:'0 auto' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <div style={{ fontSize:13, color:T.inkSoft }}>Review Pass · note {Math.min(position, total)} of {total} · {done} cosigned</div>
        <button style={btn(false)} onClick={() => { setPassing(false); load(); }}>Exit pass</button>
      </div>
      {flash === 'advance' && (
        <div style={{ background:T.rose, color:'#fff', borderRadius:8, padding:'10px 16px', marginBottom:10, fontWeight:600, fontSize:13.5 }}>
          ✓ Cosigned — next up
        </div>
      )}
      {err && <div style={{ background:'#fee2e2', color:'#991b1b', borderRadius:8, padding:'9px 14px', marginBottom:10, fontSize:13 }}>{err}</div>}

      {sigEnrolled && !pinHeld && (
        <div style={{ ...card, padding:'12px 16px', marginBottom:12, display:'flex', gap:12, alignItems:'center' }}>
          <div style={{ fontSize:13, color:T.ink, fontWeight:600 }}>Enter your signature PIN once — it applies to each cosign in this pass:</div>
          <input type="password" inputMode="numeric" maxLength={4} autoComplete="off"
            value={pin} onChange={e => setPin(e.target.value.replace(/\D/g,''))}
            placeholder="PIN" style={{ ...input, width:100, fontSize:18, letterSpacing:6, textAlign:'center' }} />
        </div>
      )}

      {cur.kind === 'group' && (
        <div style={{ ...card, padding:'16px 20px', marginBottom:12, borderLeft:`4px solid ${T.rose}` }}>
          <div style={label}>Group Session · {cur.session_date ? String(cur.session_date).slice(0,10) : ''} · {cur.author_name}</div>
          <div style={{ fontSize:15, fontWeight:700, color:T.ink, margin:'4px 0 8px' }}>{cur.topic || 'Group session'}</div>
          {cur.curriculum_narrative && (
            <div style={{ fontSize:13.5, lineHeight:1.6, color:T.ink, whiteSpace:'pre-wrap' }}>{cur.curriculum_narrative}</div>
          )}
          <div style={{ fontSize:12, color:T.inkSoft, marginTop:8 }}>Shared session content above — each client's individual section below carries its own cosignature.</div>
        </div>
      )}

      {remaining.map(note => (
        <div key={note.id} style={{ ...card, padding:'16px 20px', marginBottom:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
            <div style={{ fontSize:15, fontWeight:700, color:T.ink }}>
              {TYPE_LABEL[note.note_type] || note.note_type} — {note.patient_name || '—'}
            </div>
            <div style={{ fontSize:12, color:T.inkSoft }}>by {note.author_name} · waiting {ago(note.hours_waiting)}</div>
          </div>
          <div style={{ margin:'12px 0' }}>
            <NoteBody note={note} />
          </div>
          {readOnly ? (
            <div style={{ fontSize:12.5, color:T.inkSoft }}>Waiting on this cosigner — oversight view is read only.</div>
          ) : kickFor === note.id ? (
            <div style={{ background:T.b100, borderRadius:8, padding:'10px 12px' }}>
              <input value={kickReason} onChange={e => setKickReason(e.target.value)}
                placeholder="Reason for kick-back (required — goes to the author)"
                style={{ ...input, width:'100%', marginBottom:8 }} />
              <div style={{ display:'flex', gap:8 }}>
                <button style={btn(true)} disabled={busy === note.id} onClick={() => kickBack(note)}>Send back to author</button>
                <button style={btn(false)} onClick={() => { setKickFor(null); setKickReason(''); }}>Cancel</button>
              </div>
            </div>
          ) : declineFor === note.id ? (
            <div style={{ background:T.b100, borderRadius:8, padding:'10px 12px' }}>
              <input value={declineReason} onChange={e => setDeclineReason(e.target.value)}
                placeholder="Why is this not yours to cosign? (required — notifies leadership to fix the routing)"
                style={{ ...input, width:'100%', marginBottom:8 }} />
              <div style={{ display:'flex', gap:8 }}>
                <button style={btn(true)} disabled={busy === note.id} onClick={() => decline(note)}>Remove from my queue</button>
                <button style={btn(false)} onClick={() => { setDeclineFor(null); setDeclineReason(''); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ display:'flex', gap:10 }}>
              <button style={btn(true)} disabled={busy === note.id || (sigEnrolled && !pinHeld && pin.length !== 4)}
                onClick={() => cosign(note)}>
                {busy === note.id ? 'Signing…' : 'Cosign'}
              </button>
              <button style={btn(false)} onClick={() => { setKickFor(note.id); setDeclineFor(null); setErr(''); }}>Kick back</button>
              <button style={btn(false)} onClick={() => { setDeclineFor(note.id); setKickFor(null); setErr(''); }}>Not my cosign</button>
              {onOpenChart && <button style={{ ...btn(false), marginLeft:'auto' }} onClick={() => onOpenChart(note.patient_id)}>Open chart</button>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
